import {
    type ILineItemResponse,
    LineItem,
    LineItemType,
} from "legacyComponents/LineItemContainer.types";
import moment from "moment";

import { type TBTStatusBadgeDisplayTypes } from "@btnet/core-components/BTStatusBadge";
import { allowancesCreateChangeOrderLineItemAsAllowancePostV2 } from "@btnet/services-btnet/handlers/allowances/allowances";
import {
    lineItemsAddChangeOrderLineItemsPostV2,
    lineItemsUpdateChangeOrderLineItemPutV2,
} from "@btnet/services-btnet/handlers/line-items/line-items";
import type { AddScopeOfWorkLineItemsRequest } from "@btnet/services-btnet/handlers/models/addScopeOfWorkLineItemsRequest";
import type { ChangeOrdersPermissions } from "@btnet/services-btnet/handlers/models/changeOrdersPermissions";
import type { LineItemUpdateRequest } from "@btnet/services-btnet/handlers/models/lineItemUpdateRequest";
import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";
import type { ProposalCostCategoryResponse } from "@btnet/services-btnet/handlers/models/proposalCostCategoryResponse";
import { ProposalDisplayColumnTypes } from "@btnet/services-btnet/handlers/models/proposalDisplayColumnTypes";
import { ProposalGroupTypes } from "@btnet/services-btnet/handlers/models/proposalGroupTypes";
import { ProposalStatus } from "@btnet/services-btnet/handlers/models/proposalStatus";

import { BTServiceDropdownTypes } from "types/apiResponse/apiResponse";
import { BTLocalStorage } from "types/btStorage";
import { AccountingIntegrationType, CostTypes, MarkedAs, SignaturePrefs } from "types/enum";

import { daysFromNow } from "utilities/date/date";
import {
    getCostCodeSelectItemsFromLineItems,
    getCostTypesMarkupFromLineItemDefaults,
    getMarkupMarginDefaultsFromLineItemDefaults,
    getOwnerPriceRawTotal,
} from "utilities/lineItem/lineItemHelper";
import { isTaxGroupIdValid } from "utilities/tax/tax.utilities";

import { getFormatLineItemsFromFormatItemList } from "commonComponents/utilities/ProposalBaseLineItemContainer/WorksheetLineItemContainer.utility";

import {
    type ChangeOrderEntity,
    type ChangeOrderLineItem,
} from "entity/changeOrder/ChangeOrder/ChangeOrder.api.types";
import {
    ChangeOrderStatuses,
    decidedStatuses,
    deleteStatuses,
    nonEditableStatuses,
    releaseableStatuses,
    undecidedStatuses,
    unreleaseableStatuses,
} from "entity/changeOrder/common/types";
import {
    type FormatItemList,
    ProposalCategory,
    UnassignedCostGroupId,
    type WorksheetLineItem,
} from "entity/estimate/common/estimate.common.types";
import { type IProposalFormatFormValues } from "entity/estimate/common/ProposalFormat/ProposalFormat.types";
import { type EstimateResponse } from "entity/estimate/Estimate/Estimate.api.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

export const isChangeOrderEditable = (
    changeOrder: ChangeOrderEntity,
    permissions: ChangeOrdersPermissions | undefined,
): boolean => {
    return !!(
        (permissions?.canAdd &&
            (changeOrder.id === 0 ||
                changeOrder.approvalStatus === ChangeOrderStatuses.OWNER_REQUESTED)) ||
        (permissions?.canUpdate && changeOrder.id !== 0)
    );
};

export const isChangeOrderAlwaysInEditMode = (
    changeOrder: ChangeOrderEntity,
    permissions: ChangeOrdersPermissions | undefined,
): boolean => {
    return (
        isChangeOrderEditable(changeOrder, permissions) &&
        [
            ChangeOrderStatuses.UNRELEASED,
            ChangeOrderStatuses.OWNER_REQUESTED,
            ChangeOrderStatuses.PENDING,
        ].includes(changeOrder.approvalStatus)
    );
};

export const getChangeOrderOwnerPrice = (worksheetFormatItems: ProposalCategory[]) => {
    const worksheetLineItems = getFormatLineItemsFromFormatItemList(worksheetFormatItems);
    return getOwnerPriceRawTotal(worksheetLineItems);
};

export const transformWorksheetLineItemsToLineItems = (formatItems: FormatItemList) => {
    const worksheetLineItems = getFormatLineItemsFromFormatItemList(formatItems);
    return worksheetLineItems.map(
        (wli) =>
            new LineItem(
                {
                    id: wli.id,
                    costCodeId: wli.costCodeId,
                    costCode: wli.costCodeId!,
                    costCodeItemId: wli.costItemId,
                    catalogItemId: wli.costItemId,
                    costTypeId: 0,
                    calculatedAmount: wli.builderCost,
                    unitCost: wli.unitCost,
                    quantity: wli.quantity,
                    unitType: wli.unit,
                    markupColumn: wli.markupType,
                    margin: wli.margin,
                    markupPercentage: wli.markupPercent,
                    markupPerUnit: wli.markupPerUnit,
                    markupPrice: wli.markupAmount,
                    ownerPrice: wli.ownerPrice,
                    title: wli.itemTitle,
                    description: wli.description,
                    internalNotes: wli.internalNotes,
                    costTypes: wli.costTypes,
                    markedAs: wli.markedAs,
                    relatedGeneralItemId: wli.relatedGeneralItemId,
                    vendorType: wli.vendorType,
                    vendorProductId: wli.vendorProductId,
                    isDiscontinued: wli.isDiscontinued,
                    taxGroupId: wli.taxGroupId,
                    totalWithTax: wli.totalWithTax,
                } as ILineItemResponse,
                "",
                LineItemType.ChangeOrder,
            ),
    );
};

export function getChangeOrderStatusType(status: ChangeOrderStatuses): TBTStatusBadgeDisplayTypes {
    switch (status) {
        case ChangeOrderStatuses.PENDING:
            return "warning";
        case ChangeOrderStatuses.APPROVED:
        case ChangeOrderStatuses.MANUAL:
            return "success";
        case ChangeOrderStatuses.DECLINED:
        case ChangeOrderStatuses.MANUAL_DECLINE:
        case ChangeOrderStatuses.EXPIRED:
            return "danger";
        default:
            return "default";
    }
}

export function getChangeOrderStatusText(status: ChangeOrderStatuses): string {
    switch (status) {
        case ChangeOrderStatuses.PENDING:
            return "Pending";
        case ChangeOrderStatuses.APPROVED:
        case ChangeOrderStatuses.MANUAL:
            return "Approved";
        case ChangeOrderStatuses.DECLINED:
        case ChangeOrderStatuses.MANUAL_DECLINE:
            return "Declined";
        case ChangeOrderStatuses.EXPIRED:
            return "Expired";
        case ChangeOrderStatuses.OWNER_REQUESTED:
            return "Owner Requested";
        default:
            return "Draft";
    }
}

/**
 * When a CO with a related proposal is printed, the print view renders the
 * proposal, whose status can have diverged from the CO's after the CO is
 * decided. For decided CO statuses, return the matching ProposalStatus so
 * callers can override the displayed status; return null for statuses that
 * should keep the proposal's own status (Pending/Expired/etc.).
 */
export function getProposalStatusForCoPrint(
    approvalStatus: ChangeOrderStatuses,
): ProposalStatus | null {
    switch (approvalStatus) {
        case ChangeOrderStatuses.DECLINED:
            return ProposalStatus.Declined;
        case ChangeOrderStatuses.MANUAL_DECLINE:
            return ProposalStatus.ManualDecline;
        case ChangeOrderStatuses.APPROVED:
            return ProposalStatus.Approved;
        case ChangeOrderStatuses.MANUAL:
            return ProposalStatus.ManualApprove;
        default:
            return null;
    }
}

export const hasChangeOrderStatus = (
    changeOrder: ChangeOrderEntity,
    statuses: ChangeOrderStatuses[],
): boolean => {
    return statuses.some((s) => s === changeOrder.approvalStatus);
};

export const isChangeOrderUndecided = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, undecidedStatuses);
};

export const isChangeOrderInNonEditableState = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, nonEditableStatuses);
};

/**
 * Columns that remain editable while a change order is in a non-editable state
 * (Pending / Approved / Declined / Manual / Manual-Decline / Expired). Other
 * columns are locked in both the inline worksheet (via `canFieldBeEditedInSentState`)
 * and the small-screen modal (via `permittedEditFields` on `LineItemDetails`).
 */
export const sentStateEditableFields: readonly ProposalDisplayColumnTypes[] = [
    ProposalDisplayColumnTypes.Quantity,
    ProposalDisplayColumnTypes.UnitCost,
    ProposalDisplayColumnTypes.InternalNotes,
];

export const hasChangeOrderUnreleaseableStatus = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, unreleaseableStatuses);
};

export const hasChangeOrderReleaseableStatus = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, releaseableStatuses);
};

export const hasChangeOrderResetToPendingStatus = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, decidedStatuses);
};

export const hasChangeOrderDeleteStatus = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, deleteStatuses);
};

export const isChangeOrderDeclined = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, [
        ChangeOrderStatuses.DECLINED,
        ChangeOrderStatuses.MANUAL_DECLINE,
    ]);
};

export const isChangeOrderApproved = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, [
        ChangeOrderStatuses.APPROVED,
        ChangeOrderStatuses.MANUAL,
    ]);
};

export const isChangeOrderOwnerApproved = (changeOrder: ChangeOrderEntity): boolean => {
    return changeOrder.approvalStatus === ChangeOrderStatuses.APPROVED;
};

export const isChangeOrderOwnerDecided = (changeOrder: ChangeOrderEntity): boolean => {
    return hasChangeOrderStatus(changeOrder, [
        ChangeOrderStatuses.APPROVED,
        ChangeOrderStatuses.DECLINED,
    ]);
};

export const isChangeOrderNew = (changeOrder: ChangeOrderEntity): boolean => {
    return changeOrder.id === 0;
};

export const getChangeOrderStep = (
    changeOrder: ChangeOrderEntity,
):
    | "new"
    | "owner_requested"
    | "expired"
    | "not_sent"
    | "sent_or_pending"
    | "approved"
    | "declined"
    | null => {
    const deadline = moment(changeOrder.deadLine).startOf("day");
    const today = moment().startOf("day");
    const isExpired = deadline.isBefore(today);

    if (isChangeOrderNew(changeOrder)) {
        return "new";
    } else if (changeOrder.approvalStatus === ChangeOrderStatuses.OWNER_REQUESTED) {
        return "owner_requested";
    } else if (changeOrder.approvalStatus === ChangeOrderStatuses.UNRELEASED) {
        return "not_sent";
    } else if (changeOrder.approvalStatus === ChangeOrderStatuses.PENDING) {
        return "sent_or_pending";
    } else if (isChangeOrderApproved(changeOrder)) {
        return "approved";
    } else if (isChangeOrderDeclined(changeOrder)) {
        return "declined";
    } else if (isExpired) {
        return "expired";
    }
    return null;
};

/**
 * If a change order has a status of expired OR it has a status of pending
 * while being past the deadline date (if one exists), then it is expired.
 */
export const isChangeOrderExpired = (changeOrder: ChangeOrderEntity): boolean => {
    if (changeOrder.approvalStatus === ChangeOrderStatuses.EXPIRED) {
        return true;
    }

    if (changeOrder.approvalStatus === ChangeOrderStatuses.PENDING && changeOrder.deadLine) {
        return daysFromNow(changeOrder.deadLine) < 0;
    }

    return false;
};

export const getChangeOrderSelectedSignaturePref = (
    changeOrder: ChangeOrderEntity,
): SignaturePrefs | null => {
    if (!changeOrder.signaturePrefs) {
        return null;
    }
    return Number(changeOrder.signaturePrefs?.filter((x) => x.selected)[0].id) as SignaturePrefs;
};

export const isChangeOrderSelectedSignaturePrefValid = (
    changeOrder: ChangeOrderEntity,
): boolean => {
    return (
        getChangeOrderSelectedSignaturePref(changeOrder) !== null &&
        getChangeOrderSelectedSignaturePref(changeOrder) !== SignaturePrefs.None
    );
};

// The user should only be able to save if they have edit permissions on COs.
// However, even if a builder doesn't have Edit permission, they should be able to save
// if they have Add permissions and are creating a new change order, (coId === 0)
export const canSaveChangeOrder = (changeOrder: ChangeOrderEntity): boolean => {
    return (
        changeOrder.canEdit ||
        changeOrder.canEditWithoutPermission ||
        (changeOrder.canAdd && isChangeOrderNew(changeOrder))
    );
};

/** Default columns for change order worksheet display */
const defaultChangeOrderDisplayColumns: ProposalDisplayColumnTypes[] = [
    ProposalDisplayColumnTypes.Description,
    ProposalDisplayColumnTypes.InternalNotes,
    ProposalDisplayColumnTypes.CostType,
    ProposalDisplayColumnTypes.UnitCost,
    ProposalDisplayColumnTypes.Quantity,
    ProposalDisplayColumnTypes.Unit,
    ProposalDisplayColumnTypes.BuilderCost,
    ProposalDisplayColumnTypes.Markup,
    ProposalDisplayColumnTypes.UnitPrice,
    ProposalDisplayColumnTypes.OwnerPrice,
    ProposalDisplayColumnTypes.Margin,
    ProposalDisplayColumnTypes.Profit,
    ProposalDisplayColumnTypes.PercentInvoiced,
];

/** Get change order column display preferences from localStorage, or return defaults */
export function getChangeOrderColumnDisplayPreferences(
    isTaxOnCOEnabled: boolean = false,
): ProposalDisplayColumnTypes[] {
    const stored = BTLocalStorage.get("bt-numberArray-changeOrderColumnDisplayPreferences");
    if (stored) {
        // Only show tax column if effectiveTaxVersion is V1 and tax change order web flag is enabled.
        // There are flows where changing to old jobs this object will have the new tax column on old jobs, we prevent that.
        const filteredColumns = stored.filter(
            (column: ProposalDisplayColumnTypes) => column !== ProposalDisplayColumnTypes.Tax,
        );
        return isTaxOnCOEnabled ? stored : filteredColumns;
    }

    return isTaxOnCOEnabled
        ? [...defaultChangeOrderDisplayColumns, ProposalDisplayColumnTypes.Tax]
        : defaultChangeOrderDisplayColumns;
}

/** Save change order column display preferences to localStorage */
export function saveChangeOrderColumnDisplayPreferences(
    columns: ProposalDisplayColumnTypes[],
): void {
    BTLocalStorage.set("bt-numberArray-changeOrderColumnDisplayPreferences", columns);
}

// lineItemType is NOT patched here - BuildWorksheetLineItemResponse always resolves it via
// lineItem.LineItemType.GetValueOrDefault(LineItemType.ChangeOrder), so a grouped change-order line
// item's raw API value is already reliably LineItemType.ChangeOrder and can be trusted as-is.
//
// isEditable is NOT patched here either - WorksheetLineItem's constructor allowlist includes
// LineItemType.ChangeOrder, so it now defaults to editable on its own.
//
// costItemId is normalized the same way transformedLineItems below does (costItemId === 0 ? null):
// the backend uses 0 as a "no catalog item linked" sentinel rather than null. updateChangeOrderLineItem
// forwards costItemId as costCodeItemId on save via `?? null`, which doesn't catch 0 - sending a real 0
// makes LineItemsEngine.UpdateLineItemCostCodeItemLink look up catalog item id 0, which doesn't exist,
// throwing KeyNotFoundException and 500ing the save for any grouped line item with no catalog link.
//
// costCategoryId is normalized the same way: WorksheetLineItemResponse.CostCategoryId is a
// non-nullable int (it backs the Stable /v2/Proposals/{id}/worksheet/read-only contract too, so it
// can't become nullable without a breaking API change), and ChangeOrdersManager.PopulateCostCategoryIds
// leaves it at its 0 default when a line item's cost code doesn't resolve to a category. The desktop
// worksheet's cost-code dropdown filters by costCategoryId when it isn't null, so an unmapped 0 would
// filter every real cost code out of the CostCodeSelector's options for grouped line items.
const applyChangeOrderLineItemFixups = <
    T extends {
        costItemId?: number | null;
        costCategoryId?: number | null;
    },
>(
    li: T,
): T => ({
    ...li,
    costItemId: li.costItemId === 0 ? null : li.costItemId,
    costCategoryId: li.costCategoryId === 0 ? null : li.costCategoryId,
});

const withChangeOrderLineItemFixups = (
    group: ProposalCostCategoryResponse,
): ProposalCostCategoryResponse => ({
    ...group,
    lineItems: (group.lineItems ?? []).map(applyChangeOrderLineItemFixups),
    subGroups: (group.subGroups ?? []).map((sub) => ({
        ...sub,
        lineItems: (sub.lineItems ?? []).map(applyChangeOrderLineItemFixups),
    })),
});

// builderId and jobId come from the parent change order, not the line item - a ChangeOrderLineItem
// carries neither. WorksheetLineItem's constructor reads data.builderId/data.jobId and defaults them
// to 0 when absent, and updateChangeOrderLineItem later spreads the WorksheetLineItem straight into
// the save request. Omitting them here is what sent jobId: 0 on updates to an Unassigned-group line
// item (ado-284023/284025), so they're stamped on from the change order just like the grouped path
// (GetChangeOrderFormatGroups) already provides them.
const changeOrderLineItemToWorksheetLineItem =
    (builderId: number, jobId: number) => (item: ChangeOrderLineItem, index: number) => ({
        id: item.id,
        builderId,
        jobId,
        lineItemType: LineItemType.ChangeOrder,
        costCodeId: item.costCodeId ?? null,
        costCodeTitle: item.costCodeTitle ?? null,
        costCategoryId: null,
        costItemId: item.costItemId === 0 ? null : item.costItemId,
        itemTitle: item.itemTitle ?? "",
        description: item.description ?? "",
        quantity: item.quantity ?? 0,
        unit: item.unit ?? "",
        unitCost: item.unitCost ?? 0,
        builderCost: item.builderCost ?? 0,
        markupType: item.markupType ?? MarkupColumnTypes.Percentage,
        markupPercent: item.markupPercent ?? 0,
        markupPerUnit: item.markupPerUnit ?? 0,
        markupAmount: item.markupAmount ?? 0,
        margin: item.margin ?? 0,
        ownerPrice: item.ownerPrice ?? 0,
        costTypes: item.costTypes ?? [CostTypes.None],
        markedAs: item.markedAs ?? MarkedAs.None,
        displayOrder: index,
        isExpanded: item.isExpanded ?? true,
        isEditable: true,
        isSelected: false,
        internalNotes: item.internalNotes ?? "",
        productId: item.vendorProductId,
        vendorType: item.vendorType,
        discontinued: item.isDiscontinued ?? false,
        taxGroupId: item.taxGroupId,
        amountInvoiced: item.amountInvoiced ?? 0,
        retainageWithheld: item.retainageWithheld,
        totalWithTax: item.totalWithTax,
        selectionOptionGroupId: item.selectionOptionGroupId ?? null,
        formatData: { formatId: null },
    });

const setupEstimateDataFromChangeOrder = (
    changeOrder: ChangeOrderEntity,
    isTaxOnCOEnabled: boolean = false,
): EstimateResponse => {
    const {
        builderId,
        jobId,
        lineItems,
        costCategories,
        userCanInvoiceChangeOrder,
        effectiveTaxVersion,
        defaultTaxGroup,
        formatData: rawFormatData,
        changeOrderLineItems,
    } = changeOrder;
    const lineItemDefaults = lineItems?.defaults;

    // The server sends the complete group tree via formatData whenever the change order has a scope of
    // work. When formatData is empty (a CO with no scope of work), fall back to wrapping the flat
    // changeOrderLineItems in a single Unassigned group so the worksheet still renders all line items.
    const groupData: ProposalCostCategoryResponse[] =
        rawFormatData && rawFormatData.length > 0
            ? rawFormatData.map(withChangeOrderLineItemFixups)
            : [
                  {
                      id: UnassignedCostGroupId,
                      title: "Unassigned",
                      displayOrder: 0,
                      isExpanded: true,
                      lineItems: (changeOrderLineItems ?? []).map(
                          changeOrderLineItemToWorksheetLineItem(builderId, jobId),
                      ),
                      subGroups: [],
                  } as unknown as ProposalCostCategoryResponse,
              ];

    const formatData = groupData.map(
        (group) => new ProposalCategory(group, builderId, jobId, undefined, false),
    );

    let columnsToDisplay = getChangeOrderColumnDisplayPreferences(isTaxOnCOEnabled);

    if (!userCanInvoiceChangeOrder) {
        columnsToDisplay = columnsToDisplay.filter(
            (c) => c !== ProposalDisplayColumnTypes.PercentInvoiced,
        );
    }

    const estimateData: EstimateResponse = {
        canAddAllowances: false,
        canAddSelections: false,
        canUpdateOwnerInvoices: false,
        canEditBudget: false,
        canSendToHomeDepotCart: false,
        canLaunchTakeoff: false,
        hasTakeoffUser: false,
        projectConnectedToTakeoffs: false,
        takeoffPartnerId: undefined,
        takeoffProjectLink: undefined,
        takeoffProjectName: undefined,
        worksheetId: 0,
        builderId: builderId,
        jobId: jobId,
        isTemplate: false,
        worksheetDisplayPreferences: {
            estimateGroupType: ProposalGroupTypes.ProposalWorksheet,
            estimateFormatGroupType: ProposalGroupTypes.Assembly,
            columnDisplayPreferences: columnsToDisplay,
            columnPreferences: columnsToDisplay.map((c) => ({ id: c.toString(), width: 0 })),
        },
        revisedCostsDisplayPreferences: {
            estimateGroupType: ProposalGroupTypes.CostCategory,
            estimateFormatGroupType: ProposalGroupTypes.Assembly,
            columnDisplayPreferences: [],
            columnPreferences: [],
        },
        accountingIntegrationType: AccountingIntegrationType.None,
        worksheetLocked: false,
        isManuallyLocked: false,
        isSentToBudget: false,
        showRevisedCosts: false,
        proposalStatus: ProposalStatus.Unreleased,
        lockedByName: "",
        lockedByDate: undefined,
        entityAddedDate: moment(),
        contractPrice: 0,
        // Seed the markup/tax modal from the job-level line item defaults (the same source the
        // Estimate worksheet uses). Without this the modal always opened at 0% and never retained
        // the applied markup. costTypesMarkup below already reads from this same lineItemDefaults.
        ...getMarkupMarginDefaultsFromLineItemDefaults(lineItemDefaults),
        effectiveTaxVersion,
        hasSingleSelectCostTypes:
            lineItemDefaults?.costTypes?.type === BTServiceDropdownTypes.SINGLESELECT,
        costTypesMarkup: getCostTypesMarkupFromLineItemDefaults(lineItemDefaults),
        formatData: formatData,
        revisedFormatData: [],
        costCategories: costCategories || [],
        costCodes: lineItemDefaults ? getCostCodeSelectItemsFromLineItems(lineItemDefaults) : [],
        hasJobProposalFeature: false,
        costItemUpdateMessage: null,
        connectedTakeoffPartners: undefined,
        assemblies: [],
        revisedCostAssemblies: [],
        isCostPlusJob: false,
        isJobLinkedToAccounting: false,
        numberOfDraws: undefined,
        proposalTitle: "",
        defaultTaxGroup: defaultTaxGroup ?? undefined,
        isSelectionsReimaginedJob: changeOrder.isSelectionsReimaginedJob,
    };

    return estimateData;
};

// overriding formatItems to be more specific because EstimateResponse requires ProposalCategory[] and we always have the unassigned group
export interface IChangeOrderWorksheetFormValues extends IProposalFormatFormValues {
    formatItems: ProposalCategory[];
}

// A reload rebuilds ProposalCategory groups from the server response, which doesn't know about
// expand/collapse toggles made locally in client preview since the last load. Without this merge,
// every save (which reloads the change order afterward) would snap groups back to their
// server-remembered expanded state.
const mergeExpandedStateIntoFormatGroups = (
    freshFormatGroups: ProposalCategory[],
    previousFormatGroups: ProposalCategory[] | undefined,
): ProposalCategory[] => {
    if (!previousFormatGroups) {
        return freshFormatGroups;
    }

    const previousIsExpandedById = new Map(
        previousFormatGroups.map((group) => [group.id, group.isExpanded]),
    );

    return freshFormatGroups.map((group) => {
        const previousIsExpanded = previousIsExpandedById.get(group.id);
        return previousIsExpanded === undefined
            ? group
            : { ...group, isExpanded: previousIsExpanded };
    });
};

export const getChangeOrderWorksheetStateFromResponse = (
    changeOrderResponse: ChangeOrderEntity,
    isTaxOnCOEnabled: boolean = false,
    previousFormatGroups?: ProposalCategory[],
) => {
    const changeOrderData = setupEstimateDataFromChangeOrder(changeOrderResponse, isTaxOnCOEnabled);

    const formValues: IChangeOrderWorksheetFormValues = {
        columnsToDisplay: changeOrderData.worksheetDisplayPreferences.columnDisplayPreferences,
        formatItems: mergeExpandedStateIntoFormatGroups(
            changeOrderData.formatData,
            previousFormatGroups,
        ),
        newCategoryId: -2,
    };

    return {
        initialWorksheetFormValues: formValues,
        worksheetFormValues: formValues,
        changeOrderWorksheetData: { ...changeOrderData, formatData: formValues.formatItems },
    };
};

interface IChangeOrderLineItemSaveContext {
    changeOrderId: number;
    scopeOfWorkId: number;
    builderId: number;
    jobId: number;
}

// UnassignedCostGroupId (-1) is a client-only sentinel for "no group" - only a real group/sub-group
// id (from a group's + icon, ado-279462) should be sent to the API.
export const resolveLineItemParentId = (parentId: number | null | undefined): number | null =>
    parentId && parentId !== UnassignedCostGroupId ? parentId : null;

export const addChangeOrderLineItem = async (
    lineItem: WorksheetLineItem,
    context: IChangeOrderLineItemSaveContext,
    shouldHaveTaxAdded: boolean,
) => {
    const lineItemAddRequest: AddScopeOfWorkLineItemsRequest = {
        parentId: context.changeOrderId,
        scopeOfWorkId: context.scopeOfWorkId,
        lineItems: [
            {
                builderId: context.builderId,
                jobId: context.jobId,
                relatedParentId: context.changeOrderId,
                costCode: Number(lineItem.costCodeId),
                title: lineItem.itemTitle,
                description: lineItem.description,
                internalNotes: lineItem.internalNotes,
                costTypes: lineItem.costTypes,
                markedAs: lineItem.markedAs,
                quantity: lineItem.quantity ?? 0,
                unitType: lineItem.unit,
                unitCost: lineItem.unitCost,
                builderCost: lineItem.builderCost,
                markupType: lineItem.markupType ?? MarkupColumnTypes.Percentage,
                markupPercent: lineItem.markupPercent ?? 0,
                markupPerUnit: lineItem.markupPerUnit ?? 0,
                markupAmount: lineItem.markupAmount ?? 0,
                margin: lineItem.margin ?? 0,
                ownerPrice: lineItem.ownerPrice,
                taxGroupId: shouldHaveTaxAdded ? lineItem.taxGroupId : null,
                totalWithTax: shouldHaveTaxAdded ? lineItem.totalWithTax : lineItem.ownerPrice,
                pageTypeEnum: LineItemType.ChangeOrder,
                lineItemDisplayOrder: 0,
                parentId: resolveLineItemParentId(lineItem.parentId),
            },
        ],
    };

    return (await lineItemsAddChangeOrderLineItemsPostV2(lineItemAddRequest)).data;
};

export const addChangeOrderLineItemAsAllowance = async (
    lineItem: WorksheetLineItem,
    context: IChangeOrderLineItemSaveContext,
    shouldHaveTaxAdded: boolean,
) => {
    const lineItemAddRequest: AddScopeOfWorkLineItemsRequest = {
        parentId: context.changeOrderId,
        scopeOfWorkId: context.scopeOfWorkId,
        lineItems: [
            {
                builderId: context.builderId,
                jobId: context.jobId,
                relatedParentId: context.changeOrderId,
                costCode: Number(lineItem.costCodeId ?? 0),
                title: lineItem.itemTitle ?? "",
                description: lineItem.description,
                internalNotes: lineItem.internalNotes,
                costTypes: lineItem.costTypes,
                markedAs: lineItem.markedAs,
                quantity: lineItem.quantity ?? 1,
                unitType: lineItem.unit,
                unitCost: lineItem.unitCost,
                builderCost: lineItem.builderCost,
                markupType: lineItem.markupType ?? MarkupColumnTypes.Percentage,
                markupPercent: lineItem.markupPercent ?? 0,
                markupPerUnit: lineItem.markupPerUnit ?? 0,
                markupAmount: lineItem.markupAmount ?? 0,
                margin: lineItem.margin ?? 0,
                ownerPrice: lineItem.ownerPrice,
                costCodeItemId: lineItem.costItemId,
                lineItemDisplayOrder: 0,
                pageTypeEnum: LineItemType.ChangeOrder,
                taxGroupId: shouldHaveTaxAdded ? (lineItem.taxGroupId ?? null) : null,
                totalWithTax: shouldHaveTaxAdded ? lineItem.totalWithTax : undefined,
                parentId: resolveLineItemParentId(lineItem.parentId),
            },
        ],
    };

    return (await allowancesCreateChangeOrderLineItemAsAllowancePostV2(lineItemAddRequest)).data;
};

export const updateChangeOrderLineItem = async (
    lineItem: WorksheetLineItem,
    context: Pick<IChangeOrderLineItemSaveContext, "changeOrderId" | "scopeOfWorkId">,
) => {
    const lineItemUpdateRequest: LineItemUpdateRequest = {
        scopeOfWorkId: context.scopeOfWorkId,
        lineItem: {
            ...lineItem,
            relatedParentId: context.changeOrderId,
            costCode: lineItem.costCodeId ?? undefined,
            costCodeItemId: lineItem.costItemId ?? null,
            unitType: lineItem.unit,
            title: lineItem.itemTitle,
            amountInvoiced: lineItem.amountInvoiced ?? undefined,
        },
    };

    return (await lineItemsUpdateChangeOrderLineItemPutV2(lineItemUpdateRequest)).data;
};

export function hasTaxRateMismatch(
    changeOrder: ChangeOrderEntity,
    isTaxOnCOEnabled: boolean,
): boolean {
    if (!isTaxOnCOEnabled) {
        return false;
    }

    if (
        changeOrder.approvalStatus !== ChangeOrderStatuses.UNRELEASED &&
        changeOrder.approvalStatus !== ChangeOrderStatuses.OWNER_REQUESTED
    ) {
        return false;
    }

    const jobTaxGroupId = changeOrder.defaultTaxGroup?.taxGroupId ?? NoTaxId;

    const taxableLineItems = changeOrder.changeOrderLineItems?.filter((li) =>
        isTaxGroupIdValid(li.taxGroupId),
    );

    if (!taxableLineItems || taxableLineItems.length === 0) {
        return false;
    }

    if (jobTaxGroupId === NoTaxId) {
        return true;
    }

    return taxableLineItems.some((li) => li.taxGroupId !== jobTaxGroupId);
}
